import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Clock, Check, UserPlus, Mail, Calendar, Shield } from 'lucide-react'
import { useToast } from '@/hooks/useToast'
import { api } from '@/services/api'

interface PendingUser {
  id: string
  email: string
  firstName: string
  lastName: string
  authProvider: string
  createdAt: string
  updatedAt: string
  entraId?: string
  tenantId?: string
}

interface ApprovalDialogProps {
  user: PendingUser
  onApprovalComplete: () => void
}

const ApprovalDialog: React.FC<ApprovalDialogProps> = ({ user, onApprovalComplete }) => {
  const [selectedRole, setSelectedRole] = useState<string>('')
  const [branchId, setBranchId] = useState<string>('')
  const [isApproving, setIsApproving] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const { toast } = useToast()

  const handleApproval = async () => {
    if (!selectedRole) {
      toast.error('Error', 'Please select a role for the user')
      return
    }

    setIsApproving(true)
    try {
      await api.post(`/admin/users/${user.id}/approve`, {
        role: selectedRole,
        ...(selectedRole === 'GENERAL_MANAGER' && branchId && { branchId })
      })

      toast.success('User Approved', `${user.firstName} ${user.lastName} has been assigned the ${selectedRole.replace('_', ' ').toLowerCase()} role.`)

      setIsOpen(false)
      onApprovalComplete()
    } catch (error: any) {
      toast.error('Error', error.response?.data?.message || 'Failed to approve user')
    } finally {
      setIsApproving(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="gap-1">
          <Check className="h-4 w-4" />
          Approve
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Approve User</DialogTitle>
          <DialogDescription>
            Assign a role to {user.firstName} {user.lastName} ({user.email})
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="role">Role</Label>
            <Select value={selectedRole} onValueChange={setSelectedRole}>
              <SelectTrigger>
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="HR_ADMIN">HR Administrator</SelectItem>
                <SelectItem value="GENERAL_MANAGER">General Manager</SelectItem>
                <SelectItem value="SYSTEM_ADMIN">System Administrator</SelectItem>
              </SelectContent>
            </Select>
            {selectedRole && (
              <div className="text-sm text-muted-foreground">
                {selectedRole === 'HR_ADMIN' && 'Access to HR documents, policies, and employee management features.'}
                {selectedRole === 'GENERAL_MANAGER' && 'Access to operations data, branch performance metrics, and PowerBI reports.'}
                {selectedRole === 'SYSTEM_ADMIN' && 'Full system access including user management, settings, and all features.'}
              </div>
            )}
          </div>

          {selectedRole === 'GENERAL_MANAGER' && (
            <div className="space-y-2">
              <Label htmlFor="branchId">Branch ID (Optional)</Label>
              <Input
                id="branchId"
                value={branchId}
                onChange={(e) => setBranchId(e.target.value)}
                placeholder="e.g., BRANCH_001"
              />
              <div className="text-sm text-muted-foreground">
                Specify which branch this manager is responsible for.
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleApproval} disabled={isApproving || !selectedRole}>
            {isApproving ? 'Approving...' : 'Approve User'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface PendingUsersProps {
  onUserApproved?: () => void
}

const PendingUsers: React.FC<PendingUsersProps> = ({ onUserApproved }) => {
  const [pendingUsers, setPendingUsers] = useState<PendingUser[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const { toast } = useToast()

  const fetchPendingUsers = async () => {
    try {
      setIsLoading(true)
      const response = await api.get<{ data: { users: PendingUser[] } }>('/admin/users/pending')
      setPendingUsers(response.data?.users || [])
    } catch (error: any) {
      toast.error('Error', error.message || 'Failed to fetch pending users')
      setPendingUsers([])
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchPendingUsers()
  }, [])

  const handleApprovalComplete = () => {
    fetchPendingUsers() // Refresh the pending users list
    onUserApproved?.() // Refresh the main user list
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Pending User Approvals
          </CardTitle>
          <CardDescription>
            Users awaiting administrator approval to access the system
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserPlus className="h-5 w-5" />
          Pending User Approvals
          {pendingUsers.length > 0 && (
            <Badge variant="secondary" className="ml-2">
              {pendingUsers.length}
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          Users awaiting administrator approval to access the system
        </CardDescription>
      </CardHeader>
      <CardContent>
        {pendingUsers.length === 0 ? (
          <div className="text-center py-8">
            <div className="flex justify-center mb-4">
              <div className="p-3 rounded-full bg-green-100 dark:bg-green-900/20">
                <Check className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
            </div>
            <h3 className="text-lg font-semibold mb-2">All caught up!</h3>
            <p className="text-muted-foreground">
              No users are currently waiting for approval.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {pendingUsers.map((user) => (
              <div key={user.id} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold">
                        {user.firstName} {user.lastName}
                      </h4>
                      <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                        <Clock className="h-3 w-3 mr-1" />
                        Pending
                      </Badge>
                    </div>
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Mail className="h-4 w-4" />
                      {user.email}
                    </div>
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Calendar className="h-4 w-4" />
                      Registered {new Date(user.createdAt).toLocaleDateString()}
                    </div>
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Shield className="h-4 w-4" />
                      {user.authProvider === 'ENTRA_ID' ? 'Microsoft Entra ID' : user.authProvider}
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <ApprovalDialog 
                      user={user} 
                      onApprovalComplete={handleApprovalComplete}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default PendingUsers